import React, { useState, useEffect, useMemo } from 'react';
import { useLab } from '../../contexts/LabContext';
import { useTranslation } from 'react-i18next';

const QCM = ({ qcmData, onSubmit, showSolutionButton, onGetSolution }) => {
    const { t } = useTranslation();
    const { feedback } = useLab();
    const [selectedOptionId, setSelectedOptionId] = useState(null);

    // The backend provides `correct_answer` (object) and `distractors` (array of objects).
    // Build a flat list of options each time qcmData changes.
    const options = useMemo(() => {
        if (!qcmData) return [];

        const baseOptions = [
            ...(qcmData.distractors || []),
            ...(qcmData.correct_answer ? [{ ...qcmData.correct_answer, isCorrect: true }] : [])
        ];

        // Assign an id if none provided, and shuffle deterministically per render
        return baseOptions
            .map((opt, idx) => ({ id: opt.id ?? idx.toString(), text: opt.answer || opt.text || '', explanation: opt.explanation, isCorrect: opt.isCorrect || false }))
            .sort(() => Math.random() - 0.5); // simple shuffle
    }, [qcmData]);

    // Helper to find correct answer id for evaluation
    const correctOption = useMemo(() => options.find(o => o.isCorrect), [options]);

    // Reset selected option when qcmData changes
    useEffect(() => {
        setSelectedOptionId(null);
    }, [qcmData]);

    const handleOptionSelect = (optionId) => {
        setSelectedOptionId(optionId);

        if (!onSubmit) return;

        const isCorrect = optionId === correctOption?.id;
        const selectedObj = options.find(o => o.id === optionId);

        const qcmDetails = {
            question: qcmData.question,
            selectedAnswer: selectedObj?.text,
            correctAnswer: correctOption?.text,
            options: options.map(o => ({ id: o.id, text: o.text }))
        };

        onSubmit(isCorrect, qcmDetails);
    };

    if (!qcmData || !qcmData.question) {
        return (
            <div className="bg-gray-700 border border-gray-600 rounded-lg p-4 my-4">
                <h4 className="text-gray-200 mb-4 text-lg font-semibold">📝 {t('qcm_title') || 'QCM'}</h4>
                <p className="text-gray-400 italic text-center py-5">{t('no_qcm_available') || 'Aucun QCM disponible pour cette section'}</p>
            </div>
        );
    }

    return (
        <div className="bg-gray-700 border border-gray-600 rounded-lg p-4 my-4">
            <h4 className="text-gray-200 mb-4 text-lg font-semibold">📝 {t('qcm_title') || 'QCM'}</h4>

            <div className="text-gray-200 text-sm mb-4 leading-relaxed">
                <p className="font-medium text-blue-300 mb-2">{t('question') || 'Question'}:</p>
                <p>{qcmData.question}</p>
            </div>

            <div className="flex flex-col gap-2 mb-4">
                {options.map((option) => (
                    <label key={option.id} className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-gray-600 transition-colors">
                        <input
                            type="radio"
                            name="qcm-option"
                            value={option.id}
                            checked={selectedOptionId === option.id}
                            onChange={() => handleOptionSelect(option.id)}
                            className="text-blue-500 focus:ring-blue-500"
                        />
                        <span className="text-gray-200 text-sm">{option.text}</span>
                    </label>
                ))}
            </div>

            <div className="flex gap-3">
                {showSolutionButton && onGetSolution && (
                    <button
                        onClick={onGetSolution}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded font-medium text-sm transition-all"
                    >
                        {t('show_solution') || 'Voir la solution'}
                    </button>
                )}
            </div>

            {feedback && (
                <div className="mt-4 p-3 bg-gray-800 border border-gray-600 rounded text-gray-200 text-sm">
                    {feedback.message}
                </div>
            )}
        </div>
    );
};

export default QCM;
